import { buttonStyle } from 'components/buttons/buttonStyle';
import EntityView, { EntityViewSection } from 'components/entity-view/EntityView';
import { Row } from 'components/GridItemList';
import Loader from 'components/Loader';
import { DateDefaultNowrap } from 'components/misc/DateDefaultNowrap';
import PartyName from 'components/party/PartyName';
import { RecordLink } from 'components/record-chip/RecordLink';
import { Button } from 'components/ui/button';
import CopyToClipboardIcon from 'components/util/CopyToClipboard';
import HoursDisplay from 'components/util/HoursDisplay';
import TimeCardExpenseList from 'pages/timecards/expenses/TimeCardExpenseList';
import TimeCardInvoiceList from 'pages/timecards/invoices/TimeCardInvoiceList';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { FeatureFlag } from 'types/features';
import { RemunerationCalcType } from 'types/matters';
import { TimecardDetails } from 'types/timecards';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil';
import { formatPracticeAreaLabel } from 'utils/practiceAreaUtil';
import { useClassifiers } from 'utils/useClassifiers';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { useGoBack, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';
import { WHITE_OUTLINED_CLASS } from './buttons/whiteOutlinedClass';
import { MultiplierToggle } from './form/MultiplierToggle';
import { TimecardStatusLabel } from './labels/TimecardStatusLabel';
import { TimerLogsList } from './TimerLogsList';
import { timecardSecondsToHours } from './utils/timecardUtils';

type Props = {
    timecard: TimecardDetails;
};

const READ_CLASSIFIERS = [
    'TIMECARD_STATUS_TYPE',
    'TIMECARD_ACTIVITY_TYPE',
    'CURRENCY',
    'EXPENSE_TYPE',
    'EXPENSE_STATUS_TYPE',
    'INVOICE_STATUS_TYPE',
    'MULTIPLIER_TYPE',
    'REMUNERATION_CALC_TYPE',
];

/** A timecard's read rows by field, for the classic view's one list and the new design's Timecard card. */
export const useTimecardReadRows = (timecard: TimecardDetails) => {
    const { classifiers, isLoading } = useClassifiers({ codes: READ_CLASSIFIERS });
    const isUserPrivilegedToReadRemunerationTime = useHasPermissions([
        'FIELD_ACCESS.TIMECARD.REMUNERATION_TIME',
        'READ.TIMECARDS.REMUNERATION',
    ]);
    const showRemunerationTime =
        useIsFeatureEnabled(FeatureFlag.REMUNERATION_CALC_TYPE_CONTROL) && isUserPrivilegedToReadRemunerationTime;
    const { isTwoDecimalDisplayEnabled } = useTimecardPrecision();
    const isMultiplierEnabled = useIsFeatureEnabled(FeatureFlag.TC_MULTIPLIER);

    const multiplierTypes = useMemo(() => {
        const classifier = classifiers['MULTIPLIER_TYPE'];
        if (!classifier) {
            return [];
        }

        return classifier.values
            ? classifier.values
                  .map((classifierValue) => ({
                      label: getClassifierText(classifier, classifierValue.code),
                      value: classifierValue.code,
                      description:
                          classifierValue.code === 'NONE'
                              ? `${getClassifierText(classifier, classifierValue.code)} (${classifierValue.parameters ? parseFloat(classifierValue.parameters['multiplierRate'] ?? '0').toFixed(1) + 'x' : ''})`
                              : `${classifierValue.parameters ? parseFloat(classifierValue.parameters['multiplierRate'] ?? '0').toFixed(1) + 'x' : ''}`,

                      multiplierRate: classifierValue.parameters?.['multiplierRate'] ?? '0',
                  }))
                  .sort((a, b) => parseFloat(a.multiplierRate) - parseFloat(b.multiplierRate))
            : [];
    }, [classifiers]);

    const rows = useMemo(
        () =>
            ({
                client: {
                    label: t('timecards.form.client'),
                    content: timecard.matter.clientParty ? (
                        <PartyName
                            party={timecard.matter.clientParty}
                            isLink
                            useLinkWithOrigin
                            linkTarget="_blank"
                            variant="readonly-other"
                            showPartyNumber
                        />
                    ) : null,
                },
                assignment: {
                    label: t('timecards.form.assignment'),
                    content: timecard.matterAssignment?.name,
                },
                activityType: {
                    label: t('timecards.form.activity-type'),
                    content: getClassifierText(
                        classifiers['TIMECARD_ACTIVITY_TYPE'],
                        timecard?.timecardActivityTypeCode
                    ),
                },
                case: {
                    label: t('timecards.form.case'),
                    content: (
                        <RecordLink
                            className="entity-link text-fg hover:text-link w-fit no-underline hover:underline"
                            to={`/matter/${timecard.matter.id}`}
                            target="_blank"
                            label={timecard.matter.fullName}
                            withOrigin
                        />
                    ),
                },
                description: {
                    label: t('timecards.form.description'),
                    content: timecard.description,
                    button: timecard.description ? <CopyToClipboardIcon text={timecard.description} /> : undefined,
                },
                employee: {
                    label: t('timecards.form.employee'),
                    content: timecard.employee.name,
                },
                date: {
                    label: t('timecards.form.date'),
                    content: <DateDefaultNowrap dateValue={timecard.timecardDate} />,
                },
                nonBillable: {
                    label: t('timecards.form.is-non-billable'),
                    content: timecard.isNonBillable ? t('yes') : t('no'),
                },
                preserveNonBillableHours: {
                    label: t('timecards.preserve-non-billable-hours'),
                    content: timecard.preserveNonBillableHours ? t('yes') : t('no'),
                    hidden: !timecard.isNonBillable,
                },
                totalTime: {
                    label: t('timecards.total-time'),
                    content: <HoursDisplay value={timecard.timecardStats?.totalTimeInHours || 0} />,
                },
                billedTime: {
                    label: t('timecards.billed-time'),
                    content: <HoursDisplay value={timecard.timecardStats?.billedTimeInHours || 0} />,
                },
                remunerationTime: {
                    label: t('timecards.remuneration-time'),
                    content: (
                        <div className="flex flex-row gap-[0.5ch]">
                            {getClassifierText(
                                classifiers['REMUNERATION_CALC_TYPE'],
                                timecard.remunerationCalcTypeCode
                            )}
                            {timecard.remunerationCalcTypeCode === RemunerationCalcType.MANUAL_ENTRY ? (
                                <HoursDisplay
                                    value={
                                        timecardSecondsToHours(
                                            timecard.remunerationTimeInSeconds ?? 0,
                                            isTwoDecimalDisplayEnabled
                                        ) || 0
                                    }
                                />
                            ) : null}
                        </div>
                    ),
                    hidden: !showRemunerationTime,
                },
                multiplier: {
                    label: t('timecards.form.multiplier-code'),
                    content: <MultiplierToggle value={timecard.multiplierTypeCode} options={multiplierTypes} />,
                    hidden: !isMultiplierEnabled,
                },
                practiceAreas: {
                    label: t('practice-areas.practice-areas'),
                    content: timecard.practiceAreas.map((pa) => formatPracticeAreaLabel(pa)).join(', ') || '-',
                },
            }) satisfies Record<string, Row>,
        [classifiers, multiplierTypes, timecard, showRemunerationTime, isTwoDecimalDisplayEnabled, isMultiplierEnabled]
    );

    return { rows, classifiers, isLoading };
};

/** Rows with no content are left out, as the classic view always has. */
export const withContent = (rows: Row[]): Row[] =>
    rows.filter((item) => item.content !== '' && item.content !== null && item.content !== undefined);

const TimecardEntityView = ({ timecard }: Props): JSX.Element => {
    const { rows, classifiers, isLoading } = useTimecardReadRows(timecard);
    const navigateWithReturn = useNavigateWithReturnState();
    const goBack = useGoBack('/timecards');

    const data: EntityViewSection[] = useMemo(
        () => [
            {
                titleId: 'timecards.timecard',
                rows: withContent([
                    rows.client,
                    rows.assignment,
                    rows.activityType,
                    rows.case,
                    rows.description,
                    rows.employee,
                    rows.date,
                    rows.nonBillable,
                    rows.preserveNonBillableHours,
                    rows.totalTime,
                    rows.billedTime,
                    rows.remunerationTime,
                    rows.multiplier,
                    rows.practiceAreas,
                ]),
            },
            // ponytail: only shown once tracked; hidden on never-tracked timecards
            ...(timecard.timerLogs && timecard.timerLogs.length > 0
                ? [
                      {
                          titleId: 'timecards.timer-logs',
                          rows: [{ label: '', content: <TimerLogsList timerLogs={timecard.timerLogs} /> }],
                      },
                  ]
                : []),
            {
                titleId: 'invoices.connected-invoices',
                rows: [
                    {
                        label: '',
                        content: <TimeCardInvoiceList invoices={timecard?.invoices ?? []} classifiers={classifiers} />,
                    },
                ],
            },
            {
                titleId: 'expenses.expenses',
                rows: [
                    {
                        label: '',
                        content: (
                            <TimeCardExpenseList
                                timecardData={{ id: timecard.id ?? null, matterId: timecard?.matter?.id ?? null }}
                                classifiers={classifiers}
                                expenses={timecard?.expenses ?? []}
                                disableAdd
                            />
                        ),
                    },
                ],
            },
        ],
        [classifiers, rows, timecard]
    );

    const getActions = (): JSX.Element | null => {
        if (timecard.isEditable) {
            return (
                <Button
                    {...buttonStyle({ variant: 'outlined', color: 'primary', size: 'small' }, WHITE_OUTLINED_CLASS)}
                    onClick={() => navigateWithReturn(`/timecards/${timecard.id}/edit`)}
                >
                    <FormattedMessage id="edit" />
                </Button>
            );
        }

        return null;
    };

    if (isLoading) {
        return <Loader />;
    }
    return (
        <EntityView
            titleSuffixComponent={
                <TimecardStatusLabel
                    statusCode={timecard.timecardStatusTypeCode}
                    classifier={classifiers['TIMECARD_STATUS_TYPE']}
                />
            }
            data={data}
            titleId="timecards.timecard"
            actions={getActions()}
            onBack={goBack}
            createdAt={timecard.createdAt}
            createdByEmployee={timecard.createdByEmployee}
            lastModifiedAt={timecard.lastModifiedAt}
            changedByEmployee={timecard.changedByEmployee}
        />
    );
};

export default TimecardEntityView;
